import { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Container,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
  Checkbox,
  Button,
  Grid,
  CssBaseline,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Link,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
// JWT Auth imports
import { setCredentials } from "../../features/auth/authSlice";
import { useLoginMutation } from "../../features/auth/authApiSlice";
import appVersion from "../../utils/appVersion";

const LoginPage = () => {
  const userRef = useRef();
  const errRef = useRef();
  const [strUserName, setUserName] = useState("");
  const [strPassword, setPassword] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [openDocModal, setOpenDocModal] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [login, { isLoading }] = useLoginMutation();

  const appDocumentation = [
    { label: "PAUD", href: "https://drive.google.com/drive/folders/1-5PCifTY4OTKAYDAB515KJ8c3Cr6j1mX?usp=sharing" },
    { label: "Pondok", href: "https://drive.google.com/drive/folders/1VGTIFXR2SQhSizZ4tSq1Ia7WktjHiMui?usp=sharing" },
    { label: "SDIT", href: "https://drive.google.com/drive/folders/1DLVbpbRNo9aEI-45XENLjy1L3PNoS7uz?usp=sharing" },
    { label: "SMAIT", href: "https://drive.google.com/drive/folders/1PAfdaJ2xG6snewANlZenMM_-4PPZp_rv?usp=sharing" },
    { label: "SMPI", href: "https://drive.google.com/drive/folders/1x2CiXTyZbcQe68rFVCAbNJqnKFcd5UJu?usp=sharing" },
  ];

  useEffect(() => {
    userRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [strUserName, strPassword]);

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userData = await login({ strUserName, strPassword }).unwrap();
      dispatch(setCredentials({ ...userData }));
      // Always set selectedSchoolUuid to school.uuid for all users
      if (userData?.user?.school?.uuid) {
        const { setSchoolUuid } = await import("../../features/auth/authSlice");
        dispatch(setSchoolUuid(userData.user.school.uuid));
      }
      setUserName("");
      setPassword("");
      navigate(from, { replace: true });
    } catch (err) {
      if (!err?.originalStatus) {
        setErrMsg("Tidak ada respon dari server");
      } else if (err.originalStatus === 400) {
        setErrMsg("Username atau Password wajib diisi");
      } else if (err.originalStatus === 401) {
        setErrMsg("Akses Ditolak");
      } else {
        setErrMsg("Gagal Masuk");
      }
      if (errRef.current) {
        errRef.current.focus();
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "radial-gradient(circle, #6ba9a8, #9e99a9)",
      }}
    >
      <CssBaseline />
      <Container maxWidth="xs">
        <Paper elevation={10} sx={{ p: 4, borderRadius: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Masuk
            </Typography>
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1, width: "100%" }}
            >
              {errMsg && (
                <Typography
                  ref={errRef}
                  color="error"
                  sx={{ mb: 2, textAlign: "center" }}
                  tabIndex={-1}
                >
                  {errMsg}
                </Typography>
              )}
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={strUserName}
                inputRef={userRef}
                onChange={(e) => setUserName(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={strPassword}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={<Checkbox value="remember" color="primary" />}
                label="Remember me"
              />

              <Button
                type="button"
                fullWidth
                variant="text"
                onClick={() => setOpenDocModal(true)}
                sx={{
                  mt: 1,
                  mb: 1,
                  color: "#0d5bbf",
                  justifyContent: "flex-start",
                  fontWeight: 800,
                  textTransform: "none",
                }}
              >
                Dokumentasi Aplikasi
              </Button>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 1, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "Sign In"}
              </Button>
              <Grid container>
                <Grid item xs>
                  <Typography variant="body1">
                    Lupa password? Hubungi Tata Usaha
                  </Typography>
                </Grid>
              </Grid>

              <Typography
                variant="caption"
                sx={{ display: "block", mt: 2, textAlign: "center", color: "text.secondary" }}
              >
                Versi aplikasi v{appVersion}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Dialog open={openDocModal} onClose={() => setOpenDocModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dokumentasi Aplikasi</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
            {appDocumentation.map((doc) => (
              <Link
                key={doc.label}
                href={doc.href}
                target="_blank"
                rel="noreferrer"
                underline="hover"
                sx={{
                  p: 1.5,
                  backgroundColor: "#eef6fb",
                  borderRadius: 1.5,
                  border: "1px solid #d9e9f5",
                  color: "#1f4d76",
                  fontWeight: 600,
                }}
              >
                {doc.label}
              </Link>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
